import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback} from 'react';
import styled, {css} from 'styled-components';

import {type LegendPosition} from '../../util/plotHelpers/legendTypes';
import {InstrumentedWaveLoader} from '../utility/InstrumentedLoader';
import {type IssueStatus} from './issueMarkers';

/** Layout settings and slot content. */
type LayoutSettingsAndSlots = {
  allowIssuesAlerts?: boolean;
  fontSize: number;
  Graph: React.ReactNode;
  handleHover: (isHovered: boolean) => void;
  Legend: React.ReactNode;
  legendPosition: LegendPosition;
  loading: boolean;
  numLines: number;
  showLegend: boolean;
  /** Attached to the layout grid so the panel can measure its size. */
  sizeRef: React.Ref<HTMLDivElement>;
  Title: React.ReactNode;
  Warning: React.ReactNode;
};

export type PlotLayoutViewProps = LayoutSettingsAndSlots & {
  /** Replace the plot with the Delay Render warning. */
  delayed: boolean;
  /** Shown instead of the plot while delayed. */
  DelayWarning: React.ReactNode;
  /** Issue arrows and markers, drawn above the plot when issues are enabled. */
  IssueMarkers: React.ReactNode;
  /** Whether the layout reserves a row for issue markers. */
  issueStatus: IssueStatus;
};

const Wrapper = styled.div<
  Pick<LayoutSettingsAndSlots, 'legendPosition' | 'showLegend'> & {
    delayRender?: boolean;
    issueStatus: 'enabled' | 'disabled';
    legendHeight: number;
  }
>`
  display: grid;
  height: 100%;
  column-gap: 12px;
  row-gap: 2px;
  position: relative;
  ${({delayRender, issueStatus, legendHeight, legendPosition, showLegend}) => {
    return (
      getArea({
        delayRender,
        issueStatus,
        legendHeight,
        legendPosition,
        showLegend,
      }) ?? ''
    );
  }}
`;

const TitleWrapper = styled.div`
  grid-area: title;
`;

const WarningWrapper = styled.div`
  grid-area: warning;
`;

const IssueWrapper = styled.div`
  grid-area: issues;
`;

const GraphWrapper = styled.div`
  grid-area: graph;
  cursor: crosshair;
  height: 100%;
  position: relative;
  width: 100%;
`;

const LegendWrapper = styled.div<
  Pick<LayoutSettingsAndSlots, 'legendPosition'>
>`
  grid-area: legend;
  overflow-y: auto;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${({legendPosition}) => {
    switch (legendPosition) {
      case 'north':
      case 'south': {
        return `
          display: flex;
          flex-wrap: wrap;
        `;
      }
      default:
        return ``;
    }
  }}
`;

const AreaLayouts = (
  key: string,
  legendHeight: number
): ReturnType<typeof css> | undefined =>
  ({
    BaseAreaDelayRender: css`
      grid-template-areas:
        'title'
        'delay-render';
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr;
    `,
    BaseAreaNoIssuesNoLegend: css`
      grid-template-areas:
        'title'
        'warning'
        'graph';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) 1fr;
    `,
    BaseAreaIssuesNoLegend: css`
      grid-template-areas:
        'title'
        'warning'
        'issues'
        'graph';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) 20px 1fr;
    `,
    BaseAreaNoIssuesLegend_north: css`
      grid-template-areas:
        'title'
        'warning'
        'legend'
        'graph';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) fit-content(
          ${legendHeight}px
        ) 1fr;
    `,
    BaseAreaNoIssuesLegend_south: css`
      grid-template-areas:
        'title'
        'warning'
        'graph'
        'legend';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) 1fr fit-content(
          ${legendHeight}px
        );
    `,
    BaseAreaNoIssuesLegend_west: css`
      grid-template-areas:
        'title title'
        'warning warning'
        'legend graph';
      grid-template-columns: minmax(auto, 3fr) 7fr;
      grid-template-rows: auto fit-content(14px) 1fr;
    `,
    BaseAreaNoIssuesLegend_east: css`
      grid-template-areas:
        'title title'
        'warning warning'
        'graph legend';

      grid-template-columns: 7fr minmax(auto, 3fr);
      grid-template-rows: auto fit-content(14px) 1fr;
    `,
    BaseAreaIssuesLegend_north: css`
      grid-template-areas:
        'title'
        'warning'
        'issues'
        'legend'
        'graph';
      grid-template-columns: 1fr;
      grid-template-rows:
        auto fit-content(14px) 20px fit-content(${legendHeight}px)
        1fr;
    `,
    BaseAreaIssuesLegend_south: css`
      grid-template-areas:
        'title'
        'warning'
        'issues'
        'graph'
        'legend';
      grid-template-columns: 1fr;
      grid-template-rows: auto fit-content(14px) 20px 1fr fit-content(
          ${legendHeight}px
        );
    `,
    BaseAreaIssuesLegend_west: css`
      grid-template-areas:
        'title title'
        'warning warning'
        'issues issues'
        'legend graph';
      grid-template-columns: minmax(auto, 3fr) 7fr;
      grid-template-rows: auto fit-content(14px) 20px 1fr;
    `,
    BaseAreaIssuesLegend_east: css`
      grid-template-areas:
        'title title'
        'warning warning'
        'issues issues'
        'graph legend';
      grid-template-columns: 7fr minmax(auto, 3fr);
      grid-template-rows: auto fit-content(14px) 20px 1fr;
    `,
  })[key];

const getArea = ({
  delayRender,
  issueStatus,
  legendHeight,
  legendPosition,
  showLegend,
}: {
  delayRender?: boolean;
  issueStatus: 'enabled' | 'disabled';
  legendHeight: number;
  legendPosition: LegendPosition;
  showLegend: boolean;
}) => {
  if (delayRender) {
    return AreaLayouts('BaseAreaDelayRender', legendHeight);
  }
  const legend = showLegend ? `Legend_${legendPosition}` : 'NoLegend';
  const issues = issueStatus === 'enabled' ? 'Issues' : 'NoIssues';
  const base = `BaseArea${issues}${legend}`;

  return AreaLayouts(base, legendHeight);
};

/**
 * Arranges the title, warning, issue markers, graph, legend, and loader from
 * its props; it reads no context. Graph renders it directly and supplies the
 * slot content.
 */
export const PlotLayoutView = ({
  delayed,
  DelayWarning,
  IssueMarkers: issueMarkersSlot,
  issueStatus,
  fontSize,
  Graph,
  handleHover,
  Legend,
  legendPosition,
  loading,
  numLines,
  showLegend,
  sizeRef,
  Title,
  Warning,
  ...props
}: PlotLayoutViewProps) => {
  // Bound to both `mouseenter` and `mousemove`. `mouseenter` alone only fires
  // when the pointer crosses the boundary, so a panel that mounts under an
  // already-stationary pointer — opening the panel settings drawer while
  // hovering the chart — never registers as hovered, and LinePlotCrosshair
  // renders its non-hovered (truncated) tooltip copy instead of the real one.
  // React bails out of the re-render when the state is unchanged, so the repeat
  // calls on subsequent moves cost a function call and nothing more.
  const handleHoverStart = useCallback(() => handleHover(true), [handleHover]);

  const handleMouseLeave = useCallback(() => handleHover(false), [handleHover]);

  return (
    <TailwindContents>
      <Wrapper
        data-test="runs-line-plot-layout"
        delayRender={delayed}
        issueStatus={issueStatus}
        key="plot-layout"
        legendHeight={fontSize * 4 + 8}
        legendPosition={legendPosition}
        onMouseEnter={handleHoverStart}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleHoverStart}
        showLegend={showLegend}
        ref={sizeRef}
        {...props}>
        <TitleWrapper
          className="overflow-hidden"
          data-testid="title-wrapper"
          data-dd-action-name="Line plot title">
          {Title}
        </TitleWrapper>
        {delayed ? (
          DelayWarning
        ) : (
          <>
            <WarningWrapper
              className="flex justify-center overflow-hidden"
              data-testid="warning-wrapper">
              {Warning}
            </WarningWrapper>
            {issueStatus === 'enabled' && (
              <IssueWrapper
                className="relative flex items-center overflow-hidden"
                data-testid="issue-alerts">
                <div className="h-20">{issueMarkersSlot}</div>
              </IssueWrapper>
            )}
            <GraphWrapper
              data-test="graph-wrapper"
              data-dd-action-name="Line plot">
              {Graph}
            </GraphWrapper>
            {showLegend && (
              <LegendWrapper legendPosition={legendPosition}>
                {Legend}
              </LegendWrapper>
            )}
            {loading && (
              <>
                {numLines > 0 ? (
                  <div className="absolute z-10">
                    <InstrumentedWaveLoader
                      size="small"
                      name="panel-layout-loading-small"
                      // Delay showing the loading indicator by 50ms to prevent flickering.
                      // When data is cached, it often loads within 50ms, so showing the loader
                      // immediately would result in a brief flash. This delay ensures we only
                      // show the loader for meaningful loading states.
                      delayBeforeShow={50}
                    />
                  </div>
                ) : (
                  <div className="absolute flex h-full w-full items-center justify-center ">
                    <InstrumentedWaveLoader
                      name="panel-layout-loading-huge"
                      size="huge"
                      // Delay showing the loading indicator by 50ms to prevent flickering.
                      // When data is cached, it often loads within 50ms, so showing the loader
                      // immediately would result in a brief flash. This delay ensures we only
                      // show the loader for meaningful loading states.
                      delayBeforeShow={50}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Wrapper>
    </TailwindContents>
  );
};
